import { useState, useEffect } from 'react';
import { Sparkles, Smartphone, ArrowLeftRight } from 'lucide-react';
import QuoteConfigurator from './QuoteConfigurator';
import BuybackConfigurator from './BuybackConfigurator';

const DevisSection = () => {
  const [activeTab, setActiveTab] = useState('repair');

  useEffect(() => {
    const handleOpenTab = (e) => {
      if (e.detail === 'repair' || e.detail === 'buyback') {
        setActiveTab(e.detail);
      }
    };
    window.addEventListener('allorep:open-devis-tab', handleOpenTab);
    return () => window.removeEventListener('allorep:open-devis-tab', handleOpenTab);
  }, []);

  return (
    <section className="section-spacing relative overflow-hidden" data-testid="devis-section" id="devis">
      {/* Background */}
      <div className="absolute inset-0 gradient-section-blue" />

      {/* Decorative blobs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gradient-to-br from-cyan-400/20 to-green-400/20 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full mb-6">
            <Sparkles size={16} className="text-blue-500" />
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider" data-testid="devis-label">
              Devis & Rachat
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6" data-testid="devis-title">
            Réparation ou rachat,{' '}
            <span className="gradient-text">c'est vous qui choisissez</span>
          </h2>
          <p className="text-lg text-slate-600" data-testid="devis-description">
            Obtenez un devis de réparation instantané ou estimez la valeur de rachat de votre iPhone.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex justify-center">
            <div className="inline-flex bg-white rounded-2xl p-1.5 shadow-lg shadow-slate-200/50 border border-slate-100" data-testid="devis-tab-switcher">
              <button
                onClick={() => setActiveTab('repair')}
                className={`flex items-center gap-2.5 px-6 py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 ${
                  activeTab === 'repair'
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
                data-testid="tab-repair"
              >
                <Sparkles size={18} />
                Devis Réparation
              </button>
              <button
                onClick={() => setActiveTab('buyback')}
                className={`flex items-center gap-2.5 px-6 py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 ${
                  activeTab === 'buyback'
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-500/30'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
                data-testid="tab-buyback"
              >
                <ArrowLeftRight size={18} />
                Rachat iPhone
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto">
          <div className="relative group">
            {/* Card glow */}
            <div className={`absolute -inset-1 bg-gradient-to-r ${
              activeTab === 'repair'
                ? 'from-blue-500 to-indigo-600'
                : 'from-emerald-500 to-green-600'
            } rounded-[32px] opacity-20 blur-xl transition-all duration-500`} />

            <div className="relative bg-white rounded-3xl overflow-hidden shadow-xl">
              {/* Colored Header */}
              <div className={`bg-gradient-to-r ${
                activeTab === 'repair'
                  ? 'from-blue-500 to-indigo-600'
                  : 'from-emerald-500 to-green-600'
              } p-5 text-white relative overflow-hidden transition-all duration-500`}>
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
                </div>
                <div className="relative z-10 flex items-center justify-center gap-3">
                  {activeTab === 'repair' ? (
                    <>
                      <Sparkles size={22} />
                      <span className="font-semibold text-lg">Devis réparation iPhone</span>
                    </>
                  ) : (
                    <>
                      <Smartphone size={22} />
                      <span className="font-semibold text-lg">Estimation de rachat iPhone</span>
                    </>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="p-8">
                {activeTab === 'repair' ? (
                  <QuoteConfigurator embedded />
                ) : (
                  <BuybackConfigurator />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DevisSection;
